import React, { useEffect, useState } from 'react';
import { TextField, Button, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, MenuItem, Select, FormControl, InputLabel, Checkbox, FormControlLabel } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import useManageCompanyOrder from './useManageCompanyOrder';
import moment from 'moment-timezone';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ProfileCombo from '../../../sharedComponents/ProfileCombo';
import useGetCustomerProfile from '../../../hooks/useGetCustomerProfile';
import { formatDate } from '../../../utils/dateUtils';
import { formatAmount } from '../../../utils/formatAmount';
import ExportWithMonthSelect from '../../../utils/ExportWithMonthSelect';
import Modal from 'react-modal';
import CompanyOrderDetail from './CompanyOrderDetail';
import { toast } from 'react-toastify';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { useManageExport } from '../export/useManageExport';
import useManageStock from '../stock/useManageStock';

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)' // This sets the overlay color
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: '800px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

const customDeleteStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)' // This sets the overlay color
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

const items = [
  {
    title: 'ABB Tax No',
    name: 'abbTaxNo',
    type: 'text',
  },
]

const CompanyOrder = () => {
  const { orders, addOrderManual, getCompanyOrder, deleteCompanyOrder } = useManageCompanyOrder();
  const { profiles, getCustomerProfiles } = useGetCustomerProfile();
  const { exportOrder } = useManageExport();
  const { confirmMonthlyStock } = useManageStock();

  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState('')
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [totalAmount, setTotalAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(null);
  const [smileNameState, setSmileNameState] = useState('')
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50); // 50 items per page
  const [month, setMonth] = useState(moment().format('YYYY-MM'));
  const [isConfirmOrderOpen, setIsConfirmOrderOpen] = useState(false);
  const [isCreditCard, setIsCreditCard] = useState(false);

  const handleSelectedChange = ((smileName)=>{
    setSmileNameState(smileName)
  })

  const handleOpen = (order) => {
    setSelectedOrder(order); 
    setOpen(true);           
  };

  const handleClose = () => {
    setOpen(false);           
    setSelectedOrder(null);   
  };

  const handleAddOrder = async() => {
    if (smileNameState === '' || !paymentDate || isNaN(parseFloat(totalAmount))) {
      toast.error("Please fill in all information");
      return; // Exit if validation fails
    }

    const formattedPaymentDate = paymentDate
      ? moment(paymentDate).tz('Asia/Bangkok').format()
      : null;
    
    const response = await addOrderManual({
      smileName: smileNameState,
      totalAmount: parseFloat(totalAmount),
      paymentDate: formattedPaymentDate,
      isCreditCard: isCreditCard,
    })

    if(response === true){
      toast.success("Add new Order Success")
    }else{
      toast.error(response)
      return;
    }

    // Clear input fields
    setTotalAmount('');
    setPaymentDate(null);

    // Fetch new data to update the table
    await getCompanyOrder({ month });
  };

  const handleDelete = async () => {
    try {
      await deleteCompanyOrder(order.id);
      toast.success('Order deleted successfully!');
      await getCompanyOrder({ month });
      closeConfirm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const openConfirm = async (id) => {
    const orderToDelete = orders.find(w => w.id === id);
    if(orderToDelete) {
      setOrder(orderToDelete);
      setIsConfirmOpen(true)
    }
  };

  const closeConfirm = (id) =>{
    setIsConfirmOpen(false)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const handleMonthChange = (event) => {
    setMonth(event.target.value);
  };

  const handleExport = async () => {
    const response = await exportOrder({month});
    if(response === true){
      toast.success("Export successful!");
    }else{
      toast.error(response);
    }
  };

  const handleConfirmOrder = async () => {
    try {
      const response = await confirmMonthlyStock(month);
      if (response === true) {
        toast.success('Order confirmed successfully!');
        setIsConfirmOrderOpen(false);
      } else {
        toast.error(response);
      }
    } catch (error) {
      toast.error('Failed to confirm order');
    }
  };

  useEffect(() => {
    getCompanyOrder({ month });
  }, [month]);

  useEffect(() => {
    getCustomerProfiles();
  }, []);

  return (
    <div>
      <h2>Company Order</h2>
      <Modal
        isOpen={open}
        onRequestClose={handleClose}
        style={customStyles}
        ariaHideApp={false}
      >
        <CompanyOrderDetail order={selectedOrder}/>
      </Modal>
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customDeleteStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm
          items={items} 
          data={order} 
          closeConfirm={closeConfirm}
          confirmFunction={handleDelete}
        />
      </Modal>
      <Modal
        isOpen={isConfirmOrderOpen}
        onRequestClose={() => setIsConfirmOrderOpen(false)}
        style={customDeleteStyles}
        ariaHideApp={false}
      >
        <div className="p-4">
          <h2>Confirm Order</h2>
          <p>Are you sure you want to confirm the order for {month}?</p>
          <div className="flex justify-end space-x-2 mt-4">
            <Button 
              variant="contained" 
              color="inherit" 
              onClick={() => setIsConfirmOrderOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleConfirmOrder}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
      <div style={{ marginBottom: '10px' }}>
        <div className='flex justify-between'>
          <div className='flex space-x-3'>
            <div className='w-72'>
              <ProfileCombo 
                profiles={profiles} 
                onChange={handleSelectedChange} 
              />
            </div>
            <TextField
              label="Total Amount"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Payment Date"
                value={paymentDate}
                onChange={(newDate) => setPaymentDate(newDate)}
                />
            </LocalizationProvider>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCreditCard}
                  onChange={(e) => setIsCreditCard(e.target.checked)}
                  color="primary"
                />
              }
              label="Credit Card"
            />
            <Button variant="contained" color="primary" onClick={handleAddOrder} style={{ marginBottom: '20px' }}>
              Add Order
            </Button>
          </div>
          <div className='flex space-x-2'>
            <Button
              style={{ marginRight: '10px', height: '55px'}}
              variant="contained"
              color="primary"
              onClick={() => setIsConfirmOrderOpen(true)}
            >
              Confirm Order
            </Button>
            <ExportWithMonthSelect 
              month={month}
              handleMonthChange={handleMonthChange}
              handleExport={handleExport}
            />
          </div>
        </div>
      </div>

      <Table>
        <TableHead>
          <TableRow style={{ backgroundColor: '#96baf2' }}>
            <TableCell>No.</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>PaymentDate</TableCell>
            <TableCell>Product Group</TableCell>
            <TableCell>Total Amount</TableCell>
            <TableCell>Payment Method</TableCell>
            <TableCell>ABB Tax No.</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order, index) => (
            <TableRow key={index}>
              <TableCell>{index+1}</TableCell>
              <TableCell>{order.CustomerProfile && order.CustomerProfile.contact}</TableCell>
              <TableCell>{formatDate(order.paymentDate)}</TableCell>
              <TableCell>{order.productGroup}</TableCell>
              <TableCell>{formatAmount(order.totalAmount)}</TableCell>
              <TableCell>
                {order.note == 'true' ? 'Credit Card' : 'Transfer'}
              </TableCell>
              <TableCell>{order.abbTaxNo}</TableCell>
              <TableCell>
                <Button variant="contained" onClick={() => handleOpen(order)}>
                  View Details
                </Button>
                <Button 
                  variant="contained" 
                  color="error" 
                  onClick={() => openConfirm(order.id)} 
                  style={{ marginLeft: '8px' }}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={orders.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  );
};

export default CompanyOrder;
